<script lang="ts">
  import { LngLatBounds, Map, NavigationControl, Popup, Marker, setWorkerUrl } from 'maplibre-gl';
  import 'maplibre-gl/dist/maplibre-gl.css';
  import MapboxDraw from "./draw-compat";
  import DrawRectangle, {DrawStyles} from "mapbox-gl-draw-rectangle-restrict-area";

  import { onMount } from "svelte";
  import { replaceQueryParameter } from "../url";
  import locationService from "../services/location-service";
  import {SearchState, WHERE_SEARCH_PARAMS} from "../services/search-service";
  import type { GeoShapeResult } from "../models/geo-shape.interface";

  import type { MapMarker } from "../models/map-marker.interface";
  import type { DataFile, DataSearchResponse } from "../models/data.interface";

  import type { LatLngTuple } from "leaflet";

  const markerTemplate = document.createElement("div");
  markerTemplate.className = "marker";
  markerTemplate.style.cssText =
    "background-image: url('../../icons/marker.svg'); width: 40px; height: 40px; background-size: 100%; fill: none;";

  // TODO: This works because MapComponent is guarded by an await in its parent component. Might want to beef this up someday.
  export let dataResults: DataSearchResponse;
  export let homrPrefix: string;
  export let mapSize = 600;
  export let isobands = null;

  setWorkerUrl(new URL(`${process.env.HUGO_BASEURL}/js/maplibre-gl-worker.mjs`).toString());
  const apiKey = process.env.ESRI_API_KEY;
  const styleUrl = `https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/styles/arcgis/streets?language=local&worldview=unitedStatesOfAmerica&token=${apiKey}`;

  let mapContainer: HTMLElement;
  let map: Map;
  let shapeResult: GeoShapeResult;

  onMount(() => {
    map = new Map({
      container: mapContainer,
      center: [-98.5795, 39.8282],
      zoom: 3,
      maxZoom: 12,
      style: styleUrl
    });

    // This doesn't work yet.
    // const basemapStyle = maplibreArcGIS.BasemapStyle.applyStyle(map, {
    //   style: "arcgis/streets", // arcgis/navigation, arcgis/imagery, open/osm-style, etc.
    //   token: apiKey
    // });

    const draw = new MapboxDraw({
      userProperties: true,
      controls: {
        polygon: true
      },
      displayControlsDefault: false,
      styles: DrawStyles,
      modes: {...MapboxDraw.modes, draw_polygon: DrawRectangle }
    });
    map.addControl(draw);

    // @ts-ignore
    map.on("draw.create", bboxtoURL);
    if ($SearchState.params.has("bbox")) {
      let bbox = $SearchState.params.get("bbox").split(",").map((cord) => Number(cord));
      map.fitBounds([
        [bbox[3], bbox[0]],
        [bbox[1], bbox[2]]
      ]);
    }

    // map.on('style.load', () => {
    //   map.setProjection({
    //     type: 'globe', // Set projection to globe
    //   });
    //   map.setSky({
    //     'atmosphere-blend': [
    //       'interpolate',
    //       ['linear'],
    //       ['zoom'],
    //       0, 1,
    //       5, 1,
    //       7, 0
    //     ]
    //   });
    // });

    map.on("load", async () => {
      let points = [] // Collect all points to later set flyTo and bounds

      if ($SearchState.params.has("bbox")) {
        let bbox = $SearchState.params.get("bbox").split(",").map((cord) => Number(cord));
        let bboxCoords = [
          [bbox[1], bbox[0]],
          [bbox[1], bbox[2]],
          [bbox[3], bbox[2]],
          [bbox[3], bbox[0]],
          [bbox[1], bbox[0]]
        ]
        points = [...points, ...bboxCoords]

        map.addSource("bbox", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                properties: {},
                geometry: {
                  coordinates: [bboxCoords],
                  type: "Polygon"
                }
              }
            ]
          }
        });

        map.addLayer({
          id: "inner-box",
          type: "fill",
          source: "bbox",
          layout: {},
          paint: {
            "fill-color": "#0080ff",
            "fill-opacity": 0.5
          }
        });

        map.addLayer({
          id: "outline",
          type: "line",
          source: "bbox",
          layout: {},
          paint: {
            "line-color": "#000",
            "line-width": 3
          }
        });
      }

      if ($SearchState.params.has("esriMagicKey")) {
        shapeResult = await locationService.getShape($SearchState.params.get("esriMagicKey")) as GeoShapeResult;
        points = [...points, ...shapeResult.shape.coordinates[0][0]]
        map.addSource("shape", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                properties: {},
                geometry: {
                  coordinates: shapeResult.shape.coordinates,
                  type: "MultiPolygon"
                }
              }
            ]
          }
        });

        map.addLayer({
          id: "inner-shape",
          type: "fill",
          source: "shape",
          layout: {},
          paint: {
            "fill-color": "#00ff00",
            "fill-opacity": 0.5
          }
        });

        map.addLayer({
          id: "outline-shape",
          type: "line",
          source: "shape",
          layout: {},
          paint: {
            "line-color": "#000",
            "line-width": 3
          }
        });
      }

      if (isobands != null) {
        map.addSource("isobands-source", {
          type: "geojson",
          data: isobands
        });

        map.addLayer({
          id: "isobands-layer",
          type: "fill",
          source: "isobands-source",
          paint: {
            "fill-color": ["get", "fill"],
            "fill-opacity": 0.8
          }
        });
      }

      const mapMarkers = dataResults?.results?.map(createMarker).filter(m => !!m);
      mapMarkers?.forEach(mapMarker => {
        points.push(mapMarker.point)
        new Marker({element: markerTemplate.cloneNode(true) as HTMLElement, anchor: "bottom"})
          .setLngLat(mapMarker.point)
          .setPopup(new Popup({ offset: 25}).setHTML(`<a href=${mapMarker.link}>${mapMarker.name}</a>`))
          .addTo(map);
      });

      if (points.length > 1) {
        const bounds = new LngLatBounds();
        points.forEach(point => bounds.extend(point));
        map.fitBounds(bounds, {padding: 100});

        // map.flyTo({
        //   center: getCenterOfPoints(points),
        //   zoom: 8
        // });
      }
      map.addControl(new NavigationControl());
    });
  });

  function createMarker(data: DataFile): MapMarker | null {
    if (!(data?.centroid?.length > 1)) {
      console.log("Error: Invalid centroid coordinates found.");
      return;
    }

    return {
      name: data.stations[0].name,
      point: [data.centroid[0], data.centroid[1]],
      link: `../station-detail?stationPrefix=${homrPrefix}&stations=${data.stations[0].id}`
    }
  }

  function bboxtoURL(e) {
    replaceQueryParameter(WHERE_SEARCH_PARAMS,
      "bbox",
      locationService.updateShapeBbox(e.features[0].geometry.coordinates as LatLngTuple[]).join(',')
    );
  }
</script>

<div class="col" id="map" style="height:{mapSize}px" bind:this={mapContainer}></div>

<style>
  #map {
    height: 600px;
  }

  :global(.maplibregl-map.mouse-add .maplibregl-canvas-container.maplibregl-interactive),
  :global(.maplibregl-map.mouse-add .maplibregl-canvas-container.maplibregl-interactive:active) {
    cursor: crosshair;
  }
</style>